// The customer portal's top-level Training section (Session AGJ, Director decision).
//
// Training already existed as a tab on each site. This page answers the question the per-site tab
// cannot: "what training do we have across all our buildings, and what is due?" A managing agent with
// eight sites had to open eight tabs to find out.
//
// It REUSES rather than reimplements: the same TrainingCard the site tab renders, the same
// ReferenceDocsSection (already tenant-global, so it needs no site and no second endpoint), and the
// same renewal rule. The one thing it must NOT reuse naively is that renewal rule's keying -- see the
// note on currentRenewalSessionIds, which now keys per site. A refresher at one building must not
// take the renewal prompt off another building's session of the same type.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { trainingRenewalState, type PortalTrainingFeedSessionDto } from "../../lib/trainingTypes";
import { PortalShell } from "./PortalShell";
import { FullScreenLoader } from "../../components/Loader";
import { ContactValue } from "../../components/ContactValue";
import { ShowAllList } from "./ShowAllList";
import { ReferenceDocsSection, TrainingCard, currentRenewalSessionIds } from "./PortalTrainingPanel";

export function PortalTrainingPage() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [sessions, setSessions] = useState<PortalTrainingFeedSessionDto[] | null>(null);
  const [cap, setCap] = useState(50);
  const [error, setError] = useState(false);
  // Keyed per site by the shared rule, so the "renewal recommended" line only ever appears on the
  // newest session of that type AT THAT BUILDING.
  const renewalIds = useMemo(() => currentRenewalSessionIds(sessions ?? []), [sessions]);

  // THE ACTIONABLE ROWS, SURFACED ABOVE THE RECORD LIST. A renewal only falls due about 730 days
  // after the session, so on a newest-first list every due or overdue row sits at the very END by
  // construction -- behind the 10-row fold, which is exactly where a managing agent opening this page
  // would never see it. Rendering them separately, and never capped, is the difference between a page
  // that reports training and one that is worth opening. The set is inherently tiny: at most one row
  // per site per type of training.
  const needsAttention = useMemo(() => {
    const list = sessions ?? [];
    return list.filter((s) => {
      if (!renewalIds.has(s.id)) return false; // a superseded session carries no live renewal
      const state = trainingRenewalState(s.renewalDueDate);
      return state === "due_soon" || state === "overdue";
    });
  }, [sessions, renewalIds]);

  useEffect(() => {
    let live = true;
    Promise.all([api.portal.me(), api.portal.training()])
      .then(([me, res]) => {
        if (!live) return;
        setUserName(me.customer.name);
        setSessions(res.sessions);
        setCap(res.cap);
      })
      .catch((err) => {
        if (!live) return;
        if (err instanceof ApiError && err.status === 401) navigate("/portal/login", { replace: true });
        else setError(true);
      });
    return () => {
      live = false;
    };
  }, [navigate]);

  async function logout() {
    try {
      await api.portal.logout();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  }

  if (error) {
    return (
      <PortalShell>
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-6 text-center">
          <h1 className="mb-2 font-heading text-2xl text-ele-white">Something went wrong</h1>
          <p className="text-ele-body">
            Please try again in a moment, or call the office on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="underline" />.
          </p>
        </div>
      </PortalShell>
    );
  }
  if (!sessions) return <FullScreenLoader />;

  return (
    <PortalShell wide userName={userName} onSignOut={logout}>
      <header className="mb-6">
        <h1 className="font-heading text-3xl tracking-wide text-ele-white">Training</h1>
        <p className="mt-1 text-ele-body">Training we have delivered to your teams, and the procedures to keep to hand.</p>
      </header>

      {/* Above the records in every state, and independent of them: the reference documents are
          tenant-global, so they are worth reading even with no session on file yet. */}
      <div className="mb-4">
        <ReferenceDocsSection />
      </div>

      {needsAttention.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 font-heading text-xl text-ele-purple">Due for renewal</h2>
          {/* Guidance, never an alarm: the cards carry the same amber renewal wording they do in the
              list below, and the same way to act. */}
          <p className="mb-3 text-sm text-ele-body">
            These sessions are due, or overdue, for a refresher. Contact us and we will arrange them.
          </p>
          <div className="space-y-3">
            {needsAttention.map((s) => (
              <TrainingCard key={`due-${s.id}`} session={s} showRenewal siteName={s.siteName} />
            ))}
          </div>
        </section>
      )}

      {needsAttention.length > 0 && <h2 className="mb-2 font-heading text-xl text-ele-purple">All training records</h2>}

      {sessions.length === 0 ? (
        // State-neutral, for the reason Session AFL recorded on the site tab: this page shows
        // COMPLETED sessions only, so a customer whose training is already booked must not be told
        // that nothing has been arranged.
        <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4 text-sm text-ele-body">
          <p className="text-ele-white">Training records appear here once a session has been completed.</p>
          <p className="mt-1">
            To arrange training for your team, contact us on{" "}
            <ContactValue value="0203 576 4760" kind="phone" className="text-ele-green hover:underline" />.
          </p>
        </div>
      ) : (
        <>
          <ShowAllList
            count={sessions.length}
            render={(shown) => (
              <div className="space-y-3">
                {sessions.slice(0, shown).map((s) => (
                  <TrainingCard key={s.id} session={s} showRenewal={renewalIds.has(s.id)} siteName={s.siteName} />
                ))}
              </div>
            )}
          />
          {/* The list is capped server-side. Saying so is the Session AFH lesson: a truncated
              statutory-training list presented as complete is worse than a short one. It names WHICH
              records are missing, because the cap is newest-first and therefore drops the oldest,
              which is where an overdue renewal would sit. The "Due for renewal" section above is
              computed from the same loaded set, so anything it could act on is already surfaced. */}
          {sessions.length >= cap && (
            <p className="mt-3 text-xs text-ele-body">
              Showing the {cap} most recent training records. Older records are not listed here: contact us if you need them.
            </p>
          )}
        </>
      )}
    </PortalShell>
  );
}
